import type { SubmissionTransition } from '@planuze/admin-contracts';
import { EmptyState } from '@planuze/ui';
import { History } from 'lucide-react';
import type { PackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { usePackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { fmtDateTime } from '../../lib/format.js';

/* Quem originou a transição (derivado no repo — ADR-0461). `publisher` reusa
   `fieldLabels.publisher` (idêntico ao rótulo de campo, sem duplicar a string no content). */
function actorText(
  actorKind: string,
  labels: PackSubmissionsContent['timeline']['actorLabels'],
  publisherLabel: string,
): string {
  if (actorKind === 'staff') return labels.staff;
  if (actorKind === 'scanner') return labels.scanner;
  if (actorKind === 'publisher') return publisherLabel;
  if (actorKind === 'system') return labels.system;
  return actorKind;
}

/* Rótulos dos estados do ciclo de vida (valores de audit — string livre; a timeline inclui
   `cancelled`/`scanning`). `switch` em vez de Record: a chave `changes_requested` é snake_case
   e useNamingConvention rejeita a propriedade (mesmo motivo de `pack-submission-status.ts`).
   Fallback: valor cru (transições podem carregar status fora do enum atual). */
function statusText(
  status: string,
  labels: PackSubmissionsContent['submissionStatusLabels'],
): string {
  switch (status) {
    case 'submitted':
      return labels.submitted;
    case 'scanning':
      return labels.scanning;
    case 'reviewing':
      return labels.reviewing;
    case 'approved':
      return labels.approved;
    case 'rejected':
      return labels.rejected;
    case 'changes_requested':
      return labels.changesRequested;
    case 'cancelled':
      return labels.cancelled;
    default:
      return status;
  }
}

interface Props {
  readonly transitions: readonly SubmissionTransition[];
}

/* Timeline imutável de transições (PUB-10 / ADR-0461). Empty = histórico indisponível
   (degrade quando a tabela de transições não existe no checkout). */
export function SubmissionTimeline({ transitions }: Props) {
  const copy = usePackSubmissionsContent();
  if (transitions.length === 0) {
    return (
      <EmptyState
        icon={<History className="size-6" />}
        title={copy.timeline.emptyTitle}
        description={copy.timeline.emptyDescription}
      />
    );
  }
  return (
    <ol className="space-y-2">
      {transitions.map((event, index) => (
        <li key={`${event.at}-${index}`} className="rounded-md border border-border bg-card p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm text-foreground">
              {`${statusText(event.fromStatus, copy.submissionStatusLabels)} → ${statusText(event.toStatus, copy.submissionStatusLabels)}`}
            </span>
            <span className="text-xs text-muted-foreground">{fmtDateTime(event.at)}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              {actorText(event.actorKind, copy.timeline.actorLabels, copy.fieldLabels.publisher)}
            </span>
            {event.actorStaffId !== undefined && event.actorStaffId !== null && (
              <span className="font-mono">{event.actorStaffId}</span>
            )}
          </div>
          {event.reason !== undefined && event.reason !== null && event.reason.length > 0 && (
            <p className="mt-2 text-sm text-foreground">{event.reason}</p>
          )}
        </li>
      ))}
    </ol>
  );
}
